import axios, { AxiosInstance } from 'axios'
import { config } from '../config'
import { getIdToken, redirectToLogin } from './auth'

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: config.apiUrl,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add auth token to requests
api.interceptors.request.use((requestConfig) => {
  const token = getIdToken()
  if (token) {
    requestConfig.headers.Authorization = `Bearer ${token}`
  }
  return requestConfig
})

// Handle 401 responses
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      redirectToLogin()
    }
    return Promise.reject(error)
  }
)

// File types
export interface FileInfo {
  key: string
  size: number
  lastModified: string
}

export interface UploadUrlResponse {
  uploadUrl: string
  key: string
}

export interface DownloadUrlResponse {
  downloadUrl: string
}

export interface ShareResponse {
  shareId: string
}

export interface AccessShareResponse {
  downloadUrl: string
  filename: string
}

// File operations
export async function listFiles(): Promise<FileInfo[]> {
  const response = await api.get<{ files: FileInfo[] }>('/files')
  return response.data.files
}

export async function getUploadUrl(
  filename: string,
  contentType: string
): Promise<UploadUrlResponse> {
  const response = await api.post<UploadUrlResponse>('/files/upload-url', {
    filename,
    contentType,
  })
  return response.data
}

export async function getDownloadUrl(key: string): Promise<string> {
  const response = await api.post<DownloadUrlResponse>('/files/download-url', { key })
  return response.data.downloadUrl
}

export async function deleteFile(key: string): Promise<void> {
  await api.delete(`/files/${encodeURIComponent(key)}`)
}

export async function createShareLink(
  key: string,
  expiresIn: number = 86400,
  password: string
): Promise<ShareResponse> {
  const response = await api.post<ShareResponse>('/share', { key, expiresIn, password })
  return response.data
}

export async function accessShare(
  shareId: string,
  password: string
): Promise<AccessShareResponse> {
  const response = await axios.post<AccessShareResponse>(
    `${config.apiUrl}/share/${shareId}/access`,
    { password },
    { headers: { 'Content-Type': 'application/json' } }
  )
  return response.data
}

// Upload file directly to S3 using presigned URL
export async function uploadFile(
  file: File,
  onProgress?: (progress: number) => void
): Promise<string> {
  // Get presigned URL
  const { uploadUrl, key } = await getUploadUrl(file.name, file.type || 'application/octet-stream')

  // Upload to S3
  await axios.put(uploadUrl, file, {
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
    },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        onProgress(progress)
      }
    },
  })

  return key
}

// Download file using presigned URL
export async function downloadFile(key: string): Promise<void> {
  const downloadUrl = await getDownloadUrl(key)

  // Create a temporary link and click it
  const link = document.createElement('a')
  link.href = downloadUrl
  link.download = key
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

export default api
